// KIR-4270: the rule tables the risk-assessment engine selects variants by.
// api-client cannot import backend policy config, so the built-in rules are
// copied here; `apps/backend/src/policies/alert-risk-text-drift.test.ts` fails
// when they drift from the policy defaults. Copy lives in `alert-risk-text-copy.ts`.
/**
 * Kirin's built-in chat security patterns (backend `DEFAULT_SECURITY_PATTERNS`,
 * shared by `maliciousPatterns` and `commandPatterns`). A finding is "Kirin's"
 * only when both its stored pattern and its name match an entry exactly.
 */
export const BUILTIN_CHAT_SECURITY_PATTERNS = [
    {
        description: 'Recursive deletion of root directory (/) or // (normalizes to /)',
        pattern: 'rm\\s+-rf\\s+/+(?:\\s|$|;|&&|\\||\'|"|\\)|`)',
        bucket: 'K1',
    },
    {
        description: 'Recursive deletion of all files',
        pattern: 'rm\\s+-rf\\s+\\*',
        bucket: 'K1',
    },
    {
        description: 'Find command with -exec flag (can execute arbitrary commands)',
        pattern: 'find\\s+[^;|&]*-exec\\s+',
        bucket: 'K6',
    },
    {
        description: 'Remote script execution via curl',
        pattern: 'curl\\s+[^|]{0,500}\\|\\s*(bash|sh|zsh|fish|ksh)',
        bucket: 'K3',
    },
    {
        description: 'Remote script execution via wget',
        pattern: 'wget\\s+[^|]{0,500}\\|\\s*(bash|sh|zsh|fish|ksh)',
        bucket: 'K3',
    },
    {
        description: 'Base64-decoded payload piped to shell (obfuscated command execution)',
        pattern: 'base64\\s+[^|]{0,500}?(?<![\\w-])(?:-[dD]|--decode)(?![\\w-])[^|]{0,500}\\|\\s*(bash|sh|zsh|fish|ksh)',
        bucket: 'K4',
    },
    {
        description: 'OpenSSL-decoded payload piped to shell (obfuscated command execution)',
        pattern: 'openssl\\s+[^|]{0,500}(?<![\\w-])-d(?![\\w-])[^|]{0,500}\\|\\s*(bash|sh|zsh|fish|ksh)',
        bucket: 'K4',
    },
    {
        description: 'Hex-decoded payload piped to shell (obfuscated command execution)',
        pattern: 'xxd\\s+[^|]{0,500}(?:(?<!-)-[a-zA-Z]*r[a-zA-Z]*(?=\\s|\\||$)|--reverse)[^|]{0,500}\\|\\s*(bash|sh|zsh|fish|ksh)',
        bucket: 'K4',
    },
    {
        description: 'Dynamic code execution',
        pattern: '(?:(?<![.\\w])(?<!->)exec|(?<!\\w)child_?process\\s*\\??\\s*\\.\\s*exec|(?<!\\w)(?:wsh|obj)?shell(?:js)?\\s*\\??\\s*\\.\\s*exec|(?<!\\w)getRuntime\\(\\s*\\)\\s*\\??\\s*\\.\\s*exec|(?<!\\w)require\\(\\s*[\'"](?:node:)?child_process[\'"]\\s*\\)\\s*\\??\\s*\\.\\s*exec)(?:\\s*\\(\\s*\\\\*(?:[bfru]{1,2})?[\'"`$@]|\\(\\s*[A-Za-z_]\\w+)',
        bucket: 'K5',
    },
    {
        description: 'Dynamic code evaluation',
        pattern: '(?<!\\w)eval(?!\\w)\\s*\\(',
        bucket: 'K5',
    },
    {
        description: 'System command execution',
        pattern: '(?<![A-Za-z0-9_])system(?:\\s*\\(\\s*(?:[bfru]{1,2})?\\\\*[\'"`$]|\\(\\s*[A-Za-z_]\\w+)',
        bucket: 'K7',
    },
    {
        description: 'Shell command execution',
        pattern: '(?<![.\\w])(?<!->)shell_exec(?:\\s*\\(\\s*\\\\*(?:[bfru]{1,2})?[\'"`$@]|\\(\\s*[A-Za-z_]\\w+)',
        bucket: 'K7',
    },
    {
        description: 'Database table deletion',
        pattern: '(?<!\\w)DROP\\s+TABLE(?!\\w)',
        bucket: 'K2',
    },
    {
        description: 'Mass data deletion',
        pattern: 'DELETE\\s+FROM\\s+\\w+\\s+WHERE\\s+1\\s*=\\s*1',
        bucket: 'K2',
    },
    {
        description: 'Reverse shell command',
        pattern: 'bash\\s+-i\\s+>&',
        bucket: 'K8',
    },
    {
        description: 'Recursive silent deletion of files (Windows CMD)',
        pattern: 'del\\s+/[sS]\\s+/[qQ]',
        bucket: 'K1',
    },
    {
        description: 'Recursive silent directory deletion (Windows CMD)',
        pattern: 'rmdir\\s+/[sS]\\s+/[qQ]',
        bucket: 'K1',
    },
    {
        description: 'Recursive deletion (PowerShell Remove-Item)',
        pattern: 'Remove-Item\\s+[^|;&]*-Recurse',
        bucket: 'K1',
    },
    {
        description: 'Dynamic code execution (PowerShell Invoke-Expression)',
        pattern: 'Invoke-Expression',
        bucket: 'K5',
    },
    {
        description: 'Dynamic code execution (PowerShell iex alias)',
        pattern: '(?<!\\w)iex(?!\\w)',
        bucket: 'K5',
    },
    {
        description: 'Remote script execution via PowerShell download-and-execute',
        pattern: 'Invoke-WebRequest[^|]*\\|[^|]*(?:iex|Invoke-Expression)',
        bucket: 'K3',
    },
    {
        description: 'Encoded PowerShell command execution',
        pattern: 'powershell\\s+-[eE](nc(odedCommand)?)?\\s+',
        bucket: 'K4',
    },
    {
        description: 'PowerShell execution policy bypass',
        pattern: 'Set-ExecutionPolicy\\s+(?:Unrestricted|Bypass)',
        bucket: 'K9',
    },
    {
        description: 'Privilege escalation via PowerShell RunAs',
        pattern: 'Start-Process\\s+[^|;&]*-Verb\\s+RunAs',
        bucket: 'K10',
    },
];
/** The six default environment-file rules (backend `DEFAULT_SECRET_FILE_PATTERNS`). */
export const DEFAULT_ENV_FILE_PATTERNS = [
    '\\.env$',
    '\\.env\\.local$',
    '\\.env\\.production$',
    '\\.env\\.development$',
    '\\.env\\.test$',
    '\\.env\\.[^/]+$',
];
/**
 * Kirin's built-in protected folders (agent `pkg/hooks/patterns.go`), with the
 * examples the D14 copy names. The agent's rule id is
 * `kirin.protected-dir.<slug>`, e.g. `.env.d/` → `env-d`.
 */
export const PROTECTED_FOLDERS = [
    {
        dir: '.ssh/',
        slug: 'ssh',
        examples: 'private keys and connection settings',
    },
    { dir: '.aws/', slug: 'aws', examples: 'AWS credentials' },
    { dir: '.azure/', slug: 'azure', examples: 'Azure sign-in data' },
    { dir: '.kube/', slug: 'kube', examples: 'cluster credentials' },
    { dir: '.docker/', slug: 'docker', examples: 'registry credentials' },
    { dir: '.gnupg/', slug: 'gnupg', examples: 'GPG secret keys' },
    {
        dir: '.git/',
        slug: 'git',
        examples: 'repository settings that can include credentials',
    },
    { dir: '.env.d/', slug: 'env-d', examples: 'environment files' },
];
/** Rule-id prefix of the agent's protected-folder findings (KIR-4974). */
export const PROTECTED_DIR_RULE_PREFIX = 'kirin.protected-dir.';
/**
 * Exact (engine, ruleId) pairs that select a credential-format variant (sheet
 * column S, "Variant scope"). Regex rule ids are the backend default patterns
 * (`DEFAULT_SECRET_PATTERNS`); gitleaks ids are catalog ids. A name never selects.
 */
export const SECRET_DETECTOR_PAIRS = [
    { engine: 'regex', ruleId: 'ghp_[a-zA-Z0-9]{36}', variant: 'C1' },
    { engine: 'gitleaks', ruleId: 'github-pat', variant: 'C1' },
    { engine: 'gitleaks', ruleId: 'github-fine-grained-pat', variant: 'C2' },
    { engine: 'regex', ruleId: 'gho_[a-zA-Z0-9]{36}', variant: 'C3' },
    { engine: 'gitleaks', ruleId: 'github-oauth', variant: 'C3' },
    { engine: 'regex', ruleId: 'ghu_[a-zA-Z0-9]{36}', variant: 'C4' },
    { engine: 'regex', ruleId: 'ghs_[a-zA-Z0-9]{36}', variant: 'C4' },
    { engine: 'gitleaks', ruleId: 'github-app-token', variant: 'C4' },
    { engine: 'regex', ruleId: 'ghr_[a-zA-Z0-9]{36}', variant: 'C5' },
    { engine: 'gitleaks', ruleId: 'github-refresh-token', variant: 'C5' },
    { engine: 'gitleaks', ruleId: 'gitlab-pat', variant: 'C6' },
    { engine: 'gitleaks', ruleId: 'gitlab-pat-routable', variant: 'C6' },
    { engine: 'regex', ruleId: 'AKIA[0-9A-Z]{16}', variant: 'C7' },
    { engine: 'gitleaks', ruleId: 'aws-access-token', variant: 'C7' },
    { engine: 'regex', ruleId: 'xox[baprs]-[0-9a-zA-Z-]{10,48}', variant: 'C8' },
    { engine: 'gitleaks', ruleId: 'slack-bot-token', variant: 'C8' },
    { engine: 'gitleaks', ruleId: 'slack-user-token', variant: 'C8' },
    { engine: 'gitleaks', ruleId: 'slack-webhook-url', variant: 'C9' },
    { engine: 'regex', ruleId: 'sk_live_[0-9a-zA-Z]{24,}', variant: 'C10' },
    { engine: 'regex', ruleId: 'sk_test_[0-9a-zA-Z]{24,}', variant: 'C11' },
    { engine: 'gitleaks', ruleId: 'stripe-access-token', variant: 'C12' },
    { engine: 'gitleaks', ruleId: 'private-key', variant: 'C13' },
];
/**
 * C13 (row 55) needs the backend default private-key regex exactly. The regex
 * holds a key header that secret scanners flag, so it is matched by its 32-bit
 * FNV-1a hash (`fnv1a`) instead of a copy. The drift test pins the hash.
 */
export const PRIVATE_KEY_REGEX_FNV1A = 0x918ea3f2;
/** The gitleaks heuristic rule with its own wording (C14a); disabled by default. */
export const GITLEAKS_GENERIC_API_KEY_RULE = 'generic-api-key';
/**
 * Gateway secret kinds the G1–G4 copy names verbatim ("the {kind} format").
 * Any other kind (a raw gitleaks description) reads "a credential pattern".
 */
export const GATEWAY_SECRET_KINDS = new Set([
    'AWS Access Key',
    'GitHub Token',
    'Private Key',
    'Slack Token',
    'Stripe API Key',
    'JSON Web Token',
]);
/** kirin_ai THREAT_LABELS (676e899) → tool-call variant, after normalization. */
export const TOOL_CALL_THREAT_LABELS = {
    'data exfiltration': 'T1',
    'credential access': 'T2',
    'destructive command': 'T3',
    'prompt injection': 'T4',
    reconnaissance: 'T5',
};
// ---------------------------------------------------------------------------
// Sentences the sheet gives in its rules column (19 Oct, column T) rather than
// in the wording columns. Each names the row it comes from.
// ---------------------------------------------------------------------------
/** Rows 62–65: a group with more than one occurrence names only the latest one. */
export const GATEWAY_LATEST_OCCURRENCE = {
    lead: 'The MCP gateway found a value matching {kindPhrase} in MCP traffic. In the most recent occurrence, it was',
    surfaces: {
        'tool-args': 'in the arguments of a call to {gatewayToolPhrase}.',
        'tool-result': 'in a result returned by {gatewayToolPhrase}.',
        'prompt-args': 'in the arguments of a prompt request to the upstream MCP server.',
        'prompt-result': 'in a prompt returned by the upstream MCP server.',
    },
    whyItMatters: 'If it is a real credential and the traffic was forwarded, the receiving side and anyone with access to its logs could see and use it.',
};
/** Rows 67–80: "What happened" when there is no pre-tool event or no client. */
export const SECRET_FILE_NO_EVENT_WHAT = 'An AI coding tool action involved {path}, {role}.';
/** Rows 68, 79, 80: the closing rule sentence, and its built-in replacements. */
export const SECRET_FILE_RULE_SENTENCE = 'The path matches a configured file-protection rule.';
/** Row 77 (D11): replaces the rule sentence with an exact default env-file ruleId. */
export const SECRET_FILE_ENV_RULE_SENTENCE = "The alert comes from Kirin's default environment-file rule.";
/**
 * Row 77 template branch (Security decision D11): an environment template file
 * by basename, case-sensitive like the detector. Its Why it matters is used as
 * written, with no rule sentence.
 */
export const SECRET_FILE_ENV_TEMPLATE = {
    basename: /^\.env(?:\.[^./]+)*\.(?:example|sample|template)$/,
    role: 'which is named like an environment template file',
    why: 'Environment template files are intended to hold example settings or placeholders. The filename does not establish what this file contains. If it contains real secrets and its contents reached the agent, those secrets could be exposed.',
};
/** Row 113: What happened when the typosquat target or signal is missing. */
export const TYPOSQUAT_NO_SIGNAL_WHAT = 'The coding agent requested a command that installs "{name}" from {ecosystem}, a name that closely imitates a popular package in that registry.';
/** Row 113: {imitation} by the detector's signal (detect-typosquat.ts). */
export const TYPOSQUAT_IMITATION = {
    'confusable-substitution': 'uses look-alike characters to imitate',
    'single-edit': 'is one keystroke away from',
};
export const SECRET_FILE_BUILTIN_RULE_SENTENCE = {
    folder: "The alert comes from Kirin's built-in protected-folder rule for {dir}, which covers every file in that folder.",
    nodeModules: "The alert comes from Kirin's built-in protected-folder rule for node_modules/, which covers every file under such a folder.",
};
/** Rows 107, 109, 111: "What happened" forms by count and type list. */
export const PII_WHAT_FORMS = {
    manyWithTypes: 'At least {n} personal-data matches, including {typeList}, were found {source}.',
    manyWithoutTypes: 'At least {n} personal-data matches were found {source}.',
    oneWithTypes: 'At least 1 personal-data match ({typeList}) was found {source}.',
    oneWithoutTypes: 'At least 1 personal-data match was found {source}.',
    noCount: 'Personal-data matches were found {source}.',
};
/** Row 107: dropped from "Why it matters" when no matched type is sensitive. */
export const PII_SENSITIVE_SENTENCE = 'At least one matched type is classed as sensitive. ';
//# sourceMappingURL=alert-risk-text.js.map